import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { wordApi } from '@/api/word.api';
import { transcriptionFrenchApi } from '@/api/transcriptionFrench.api';
import { extractErrorMessage } from '../utils';
import { existingFrenchFor, translationNavigationState, type SavedTranslations } from '../speak-session.logic';
import type { NavigateTranslationPayload } from '../components/SpeakPhraseTranslationActions';
import type { SpeakSession } from './useSpeakSession';

/**
 * Phrase edits of the "Enregistrer" page, unchanged: a moderator's inline
 * phrase fix (wordApi.updateTranslation), the local-phrase edit (applied
 * by moderators, proposed by contributors) and the trip to
 * transcription-phrase to add or edit a translation.
 */
export function useSpeakPhraseEdits(session: SpeakSession) {
  const { t, user, selectedLanguage, wordItems, setWordItems, currentSentence, page, collectorContext, setIsUploading } = session;
  const navigate = useNavigate();
  const { toast } = useToast();
  const isModerator = user?.role === 'moderator' || user?.role === 'admin';

  const [isEditingPhrase, setIsEditingPhrase] = useState(false);
  const [editedPhrase, setEditedPhrase] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [isEditingLocal, setIsEditingLocal] = useState(false);
  const [editingLocalText, setEditingLocalText] = useState('');
  const [showAfricanKeyboard, setShowAfricanKeyboard] = useState(false);

  // Reset inline edit state when sentence or page changes
  useEffect(() => {
    setIsEditingLocal(false);
    setEditingLocalText('');
  }, [currentSentence, page]);

  const handleUpdatePhrase = async () => {
    if (!editedPhrase.trim() || !selectedLanguage) return;
    try {
      await wordApi.updateTranslation(selectedLanguage, session.sentences[currentSentence], editedPhrase, 'local');
      setWordItems((prev) => {
        const next = [...prev];
        if (next[currentSentence]) next[currentSentence] = { ...next[currentSentence], text: editedPhrase };
        return next;
      });
      setIsEditingPhrase(false);
      setShowKeyboard(false);
      toast({ title: t.phraseModified, description: t.phraseModifiedDesc });
    } catch (error) {
      console.error(error);
      toast({ title: t.phraseUpdateError, description: t.phraseUpdateErrorDesc, variant: 'destructive' });
    }
  };

  const navigatePhraseTranslation = (payload: NavigateTranslationPayload) => {
    navigate(`/${session.currentLang}/transcription-phrase`, {
      state: translationNavigationState(payload, {
        fromSpeakContribute: true,
        targetLanguage: selectedLanguage,
        returnTo: session.returnToCurrentSession,
        currentPage: page,
        currentSentence,
        isModerator,
        fromCollector: collectorContext?.fromCollector,
        collectorId: collectorContext?.collectorId,
        communityProfileId: collectorContext?.communityProfileId,
        communityProfileCode: collectorContext?.communityProfileCode,
        communityProfileLocation: collectorContext?.communityProfileLocation,
        ...session.recordingDevice,
      }),
    });
  };

  const saveLocalEdit = async () => {
    try {
      setIsUploading(true);
      const newLocal = String(editingLocalText || '').trim();
      setWordItems((prev) => {
        const copy = [...prev];
        if (copy[currentSentence]) copy[currentSentence] = { ...copy[currentSentence], text: newLocal };
        return copy;
      });
      try {
        const key = session.translationsStorageKey(page);
        const raw = localStorage.getItem(key);
        const map: SavedTranslations = raw ? JSON.parse(raw) : {};
        map[String(currentSentence)] = { ...(map[String(currentSentence)] ?? {}), local: newLocal };
        localStorage.setItem(key, JSON.stringify(map));
      } catch {
        // ignore storage errors
      }
      const existingFr = existingFrenchFor(wordItems[currentSentence]);
      if (existingFr) {
        // Moderators/admins apply the change instantly; other contributors submit a reviewable proposal
        if (isModerator) {
          await wordApi.updateTranslation(selectedLanguage, existingFr, newLocal, 'local');
        } else {
          await transcriptionFrenchApi.submitPhraseTranslation(selectedLanguage, existingFr, newLocal);
        }
      }
      toast({ title: t.localSaved, description: t.localSavedDesc });
      setIsEditingLocal(false);
    } catch (err) {
      console.error(err);
      toast({ title: t.localSaveError, description: extractErrorMessage(err) || t.updateFailed, variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isModerator,
    isEditingPhrase,
    editedPhrase,
    showKeyboard,
    setEditedPhrase,
    handleUpdatePhrase,
    cancelPhraseEdit: () => {
      setIsEditingPhrase(false);
      setShowKeyboard(false);
    },
    showPhraseKeyboard: () => setShowKeyboard(true),
    startPhraseEdit: () => {
      setEditedPhrase(session.sentences[currentSentence]);
      setIsEditingPhrase(true);
    },
    /** Props of SpeakPhraseTranslationActions, minus the word item. */
    translationActions: {
      t,
      isModerator,
      isEditingLocal,
      editingLocalText,
      showAfricanKeyboard,
      selectedLanguage,
      onNavigateTranslation: navigatePhraseTranslation,
      onStartLocalEdit: (value: string) => {
        setEditingLocalText(value);
        setIsEditingLocal(true);
      },
      onEditingLocalTextChange: setEditingLocalText,
      onToggleAfricanKeyboard: () => setShowAfricanKeyboard((value) => !value),
      onSaveLocalEdit: saveLocalEdit,
      onCancelLocalEdit: () => setIsEditingLocal(false),
    },
    /** True while a text field of the card is open: action-bar keys stay off. */
    isTyping: isEditingPhrase || isEditingLocal,
  };
}

export type SpeakPhraseEdits = ReturnType<typeof useSpeakPhraseEdits>;
